import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Loader2,
  Play,
  Globe,
  Laptop,
  Check,
  X,
  AlertTriangle,
  ExternalLink,
  RefreshCw,
  Activity,
  FastForward,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import type { StoredCaptureEvent } from '../../lib/types';
import { useOptionalExecutionStore } from '../../lib/storage/provider';

import { processExecutionEvents, type ProcessedEvent } from './inspector-context';

// ─── localStorage ─────────────────────────────────────────────────────────────

function sessionKey(projectId: string): string {
  return `flux_local_agent_url_${projectId}`;
}
function readSessionUrl(projectId: string): string {
  if (typeof window === 'undefined') return '';
  return localStorage.getItem(sessionKey(projectId)) ?? '';
}
function writeSessionUrl(projectId: string, url: string): void {
  if (typeof window === 'undefined') return;
  if (url.trim()) localStorage.setItem(sessionKey(projectId), url.trim());
  else localStorage.removeItem(sessionKey(projectId));
}

// ─── Types ────────────────────────────────────────────────────────────────────

type UrlSource = 'production' | 'session';

/** "replay" replays all events deterministically. "resume" replays up to the last
 *  recorded event, then continues live — partial mode. Resume requires production URL. */
type RunMode = 'replay' | 'resume';

interface ReplayStep {
  id: string;
  seq: number;
  capability: string;
  action: string;
  phase: 'request' | 'response' | 'error';
  durationMs?: number | undefined;
  preview: string | null;
}

type ReplayState =
  | { status: 'idle' }
  | { status: 'loading' }
  | {
      status: 'running';
      replayId: string;
      pollUrl: string;
      logs: string[];
      steps: ReplayStep[];
      elapsedMs: number;
    }
  | { status: 'success'; durationMs: number; logs: string[]; steps: ReplayStep[] }
  | { status: 'error'; message: string; logs: string[]; steps: ReplayStep[] };

interface PollResponse {
  ok: boolean;
  status: 'running' | 'done' | 'error';
  replayId: string;
  elapsedMs: number;
  logs: string[];
  steps?: ReplayStep[];
  result?: unknown;
  durationMs?: number | undefined;
  error?: string;
}

function readString(obj: Record<string, unknown> | null | undefined, keys: string[]): string {
  if (!obj) return '';
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === 'string' && value.trim().length > 0) return value;
  }
  return '';
}

// ─── Step display helpers ─────────────────────────────────────────────────────

function stepLabel(capability: string, action: string, phase: string): string {
  if (capability === 'vm' && action === 'boot')
    return phase === 'request' ? 'function called' : 'function returned';
  if (capability === 'rpc') return action || 'service call';
  if (capability === 'fetch') return 'fetch';
  if (capability === 'env') return action === 'get' ? 'env access' : `env.${action}`;
  if (capability === 'console') return `console.${action}`;
  if (capability === 'crypto') return `crypto.${action}`;
  if (capability === 'math') return `math.${action}`;
  if (capability === 'checkpoint') return 'checkpoint';
  if (capability === 'timer') return `timer.${action}`;
  if (capability === 'date' || capability === 'time')
    return action === 'sleep' ? 'sleep' : `${capability}.now`;
  return `${capability}.${action}`;
}

function stepColor(capability: string): string {
  switch (capability) {
    case 'vm':
      return '#a78bfa'; // purple
    case 'fetch':
      return '#60a5fa'; // blue
    case 'console':
      return '#fbbf24'; // amber
    case 'env':
      return '#fb923c'; // orange
    case 'crypto':
      return '#2dd4bf'; // teal
    case 'math':
      return '#f472b6'; // pink
    case 'checkpoint':
      return '#4ade80'; // green
    case 'timer':
      return '#94a3b8'; // slate
    case 'abort':
      return '#f87171'; // red
    default:
      return '#6b7280'; // gray
  }
}

function cleanActionLabel(capability: string, action: string): string {
  if (!action) return action;
  let next = action;

  if (capability === 'rpc') {
    if (next.startsWith('rpc.')) next = next.slice(4);
    if (next.startsWith('rpc:')) next = next.slice(4);
    if (next.startsWith('rpc ')) next = next.slice(4);
    return next.trim() || 'service call';
  }

  if (capability === 'vm' && next.startsWith('vm.')) {
    return next.slice(3);
  }

  return next;
}

function toPreview(payload: unknown): string | null {
  if (payload === undefined || payload === null) return null;
  if (typeof payload === 'string') return payload.slice(0, 160);
  try {
    return JSON.stringify(payload).slice(0, 160);
  } catch {
    return String(payload as string | number | boolean | null | undefined).slice(0, 160);
  }
}

function toReplayStep(event: ProcessedEvent, seq: number): ReplayStep {
  const payload =
    event.phase === 'span'
      ? (event.errorPayload ?? event.responsePayload ?? event.requestPayload)
      : event.payload;
  const phase: ReplayStep['phase'] =
    event.phase === 'span' ? (Boolean(event.errorPayload) ? 'error' : 'response') : event.phase;

  return {
    id: event.id,
    seq,
    capability: event.capability,
    action: cleanActionLabel(event.capability, event.fnName ?? event.action),
    phase,
    durationMs: event.durationMs,
    preview: toPreview(payload),
  };
}

function mapExecutionSteps(events: StoredCaptureEvent[]): ReplayStep[] {
  const processed = processExecutionEvents(events);
  return processed.events.map((event, idx): ReplayStep => toReplayStep(event, idx));
}

function pickDisplaySteps(
  executionSteps: ReplayStep[],
  replaySteps: ReplayStep[] | undefined,
): ReplayStep[] {
  if (executionSteps.length > 0) return executionSteps;
  return replaySteps ?? [];
}

export interface ReplayDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  executionId: string;
  productionUrl: string | null;
  /** Duration of the original execution in ms — used for before/after comparison. */
  originalDurationMs?: number;
  /** Render the replay UI inline without modal overlay. */
  embedded?: boolean;
  /** Optional className applied to inline container. */
  embeddedClassName?: string;
  /** Optional static events payload for embedded contexts without storage worker. */
  events?: StoredCaptureEvent[] | undefined;
  /** Simulate replay/resume locally without network calls (useful for demos/marketing). */
  simulateRun?: boolean;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ReplayDialog({
  open,
  onOpenChange,
  projectId,
  executionId,
  productionUrl,
  originalDurationMs,
  embedded = false,
  embeddedClassName,
  events: providedEvents,
  simulateRun = false,
}: ReplayDialogProps): import('react/jsx-runtime').JSX.Element {
  const [selected, setSelected] = useState<UrlSource>(
    productionUrl != null ? 'production' : 'session',
  );
  const [runMode, setRunMode] = useState<RunMode>('replay');
  const [sessionUrl, setSessionUrl] = useState('');
  const [editingSession, setEditingSession] = useState(false);
  const [sessionDraft, setSessionDraft] = useState('');
  const [replay, setReplay] = useState<ReplayState>({ status: 'idle' });
  const [executionSteps, setExecutionSteps] = useState<ReplayStep[]>([]);
  const stepsEndRef = useRef<HTMLDivElement>(null);

  useEffect((): void => {
    if (!open) return;
    const saved = readSessionUrl(projectId);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSessionUrl(saved);
    setSessionDraft(saved);
    setEditingSession(!saved);
    setReplay({ status: 'idle' });
    setExecutionSteps([]);
    setRunMode('replay');
    if (productionUrl == null && !saved) setSelected('session');
    else if (productionUrl != null) setSelected('production');
  }, [open, projectId, productionUrl]);

  // Auto-scroll steps pane to bottom as new steps arrive.
  const replayStepsCount = 'steps' in replay ? replay.steps.length : 0;
  useEffect((): void => {
    if (embedded) return;
    stepsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [replayStepsCount, embedded]);

  const saveSession = useCallback((): void => {
    writeSessionUrl(projectId, sessionDraft);
    setSessionUrl(sessionDraft.trim());
    setEditingSession(false);
  }, [projectId, sessionDraft]);

  const clearSession = useCallback((): void => {
    writeSessionUrl(projectId, '');
    setSessionUrl('');
    setSessionDraft('');
    setEditingSession(true);
  }, [projectId]);

  const activeUrl = selected === 'production' ? (productionUrl ?? '') : sessionUrl;
  const isActive = replay.status === 'loading' || replay.status === 'running';
  const isDone = replay.status === 'success' || replay.status === 'error';

  const store = useOptionalExecutionStore();
  const getDetails = store?.getDetails;
  const getExecution = store?.getExecution;
  const storeLoading = store?.isLoading ?? false;

  async function runReplay(): Promise<void> {
    if (!activeUrl || (providedEvents == null && storeLoading)) return;
    setReplay({ status: 'loading' });
    // ... (rest of runReplay)
    try {
      const events = providedEvents ?? (getDetails ? await getDetails(executionId) : null);
      if (!Array.isArray(events)) {
        setReplay({
          status: 'error',
          message: `Failed to load events for replay`,
          logs: [],
          steps: [],
        });
        return;
      }

      const normalizedEvents = events as StoredCaptureEvent[];
      const mappedSteps = mapExecutionSteps(normalizedEvents);
      setExecutionSteps(mappedSteps);

      if (simulateRun) {
        const simulatedDurationMs =
          runMode === 'resume'
            ? Math.max(700, Math.round((originalDurationMs ?? 1600) * 0.72))
            : Math.max(900, Math.round((originalDurationMs ?? 1600) * 0.84));
        const simulatedReplayId = `sim-${Date.now()}`;
        setReplay({
          status: 'running',
          replayId: simulatedReplayId,
          pollUrl: '',
          logs: [
            runMode === 'resume'
              ? 'Simulated resume started from captured checkpoint.'
              : 'Simulated replay started from captured execution.',
          ],
          steps: mappedSteps,
          elapsedMs: 0,
        });
        const startMs = Date.now();
        const tick = setInterval((): void => {
          const elapsed = Date.now() - startMs;
          setReplay(
            (prev): ReplayState =>
              prev.status === 'running' && prev.replayId === simulatedReplayId
                ? { ...prev, elapsedMs: Math.min(elapsed, simulatedDurationMs) }
                : prev,
          );
          if (elapsed >= simulatedDurationMs) {
            clearInterval(tick);
            setReplay({
              status: 'success',
              durationMs: simulatedDurationMs,
              logs: [
                runMode === 'resume'
                  ? 'Simulated resume finished successfully.'
                  : 'Simulated replay finished successfully.',
              ],
              steps: mappedSteps,
            });
          }
        }, 120);
        return;
      }

      const bootEvent = events.find(
        (e): boolean =>
          (e as Record<string, unknown>)['capability'] === 'vm' &&
          (e as Record<string, unknown>)['action'] === 'boot' &&
          ((e as Record<string, unknown>)['phase'] === 'request' ||
            (e as Record<string, unknown>)['phase'] === 'span'),
      ) as Record<string, unknown> | undefined;

      const bootPayload = ((): Record<string, unknown> | undefined => {
        const payload = bootEvent?.['payload'];
        if (Boolean(payload) && typeof payload === 'object')
          return payload as Record<string, unknown>;
        const requestPayload = bootEvent?.['requestPayload'];
        if (Boolean(requestPayload) && typeof requestPayload === 'object')
          return requestPayload as Record<string, unknown>;
        return undefined;
      })();

      const summary = getExecution?.(executionId);
      const entry = readString(bootPayload, ['entry', 'name']) ?? summary?.entry ?? 'unknown';
      const res = await fetch(activeUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: runMode,
          action: runMode,
          entry,
          events: normalizedEvents,
          executionId,
          replaySource: 'dashboard',
          // Resume uses partial mode so recorded events replay then live IO continues
          mode: runMode === 'resume' ? 'partial' : 'full',
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch((): string => '');
        setReplay({
          status: 'error',
          message: `Agent returned ${res.status}${text ? ` — ${text.slice(0, 120)}` : ''}`,
          logs: [],
          steps: [],
        });
        return;
      }
      const json = (await res.json()) as {
        ok: boolean;
        replayId?: string;
        error?: string;
        data?: {
          durationMs?: number | undefined;
          logs?: string[];
          steps?: ReplayStep[];
        };
      };
      if (json.ok && json.replayId != null) {
        setReplay({
          status: 'running',
          replayId: json.replayId,
          pollUrl: activeUrl,
          logs: [],
          steps: mappedSteps,
          elapsedMs: 0,
        });
      } else if (json.ok && json.data) {
        setReplay({
          status: 'success',
          durationMs: json.data.durationMs ?? 0,
          logs: json.data.logs ?? [],
          steps: pickDisplaySteps(mappedSteps, json.data.steps),
        });
      } else if (json.ok) {
        setReplay({ status: 'success', durationMs: 0, logs: [], steps: mappedSteps });
      } else {
        setReplay({
          status: 'error',
          message: json.error ?? 'Unknown error',
          logs: [],
          steps: mappedSteps,
        });
      }
    } catch (err) {
      setReplay({
        status: 'error',
        message: err instanceof Error ? err.message : String(err),
        logs: [],
        steps: [],
      });
    }
  }

  // Poll while running.
  const pollingReplayId =
    replay.status === 'running' && !replay.replayId.startsWith('sim-') ? replay.replayId : null;
  const pollingUrl =
    replay.status === 'running' && !replay.replayId.startsWith('sim-') ? replay.pollUrl : null;
  useEffect(() => {
    if (pollingReplayId == null || pollingUrl == null) return;
    let active = true;
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    const timer = setInterval(async (): Promise<void> => {
      try {
        const res = await fetch(`${pollingUrl}/poll/${pollingReplayId}`);
        if (!res.ok || !active) return;
        const data = (await res.json()) as PollResponse;
        if (data.status === 'done') {
          clearInterval(timer);
          setReplay({
            status: 'success',
            durationMs: data.durationMs ?? data.elapsedMs ?? 0,
            logs: data.logs ?? [],
            steps: pickDisplaySteps(executionSteps, data.steps),
          });
        } else if (data.status === 'error') {
          clearInterval(timer);
          setReplay({
            status: 'error',
            message: data.error ?? 'Unknown error',
            logs: data.logs ?? [],
            steps: pickDisplaySteps(executionSteps, data.steps),
          });
        } else {
          setReplay((prev) =>
            prev.status === 'running'
              ? {
                  ...prev,
                  logs: data.logs ?? [],
                  steps: pickDisplaySteps(executionSteps, data.steps),
                  elapsedMs: data.elapsedMs ?? prev.elapsedMs,
                }
              : prev,
          );
        }
      } catch {
        /* transient — keep polling */
      }
    }, 800);
    return (): void => {
      active = false;
      clearInterval(timer);
    };
  }, [pollingReplayId, pollingUrl, executionSteps]);

  const steps =
    replay.status === 'running'
      ? replay.steps
      : replay.status === 'success'
        ? replay.steps
        : replay.status === 'error'
          ? replay.steps
          : [];

  const canRun = !!activeUrl && !isActive && (providedEvents != null || !storeLoading);

  const content = (
    <>
      <DialogHeader>
        {embedded ? (
          <h3 className="font-heading text-base leading-none font-medium">
            {runMode === 'resume' ? 'Resume Execution' : 'Replay Execution'}
          </h3>
        ) : (
          <DialogTitle>
            {runMode === 'resume' ? 'Resume Execution' : 'Replay Execution'}
          </DialogTitle>
        )}
        <p className="text-xs text-muted-foreground">
          {runMode === 'resume'
            ? 'Continue from where this execution left off — recorded events replay first, then live IO continues. Production URL only.'
            : 'Re-run this execution on your agent using the original captured inputs. Encrypted env values are decrypted locally — nothing leaves your machine.'}
        </p>
      </DialogHeader>

      {/* ── Replay / Resume mode toggle ───────────────────────────────── */}
      {!isActive && !isDone && (
        <div className="flex items-center gap-1 p-1 rounded-lg bg-muted/40 border border-border/60">
          <button
            onClick={(): void => setRunMode('replay')}
            className={cn(
              'flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all',
              runMode === 'replay'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Play className="h-3 w-3" />
            Replay
          </button>
          <button
            onClick={(): void => {
              setRunMode('resume');
              // Resume requires production URL
              if (productionUrl != null) setSelected('production');
            }}
            disabled={productionUrl == null}
            className={cn(
              'flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all',
              runMode === 'resume'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
              productionUrl == null && 'opacity-40 cursor-not-allowed',
            )}
            title={
              productionUrl == null ? 'Configure a production agent URL to use resume' : undefined
            }
          >
            <FastForward className="h-3 w-3" />
            Resume
            {productionUrl == null && (
              <span className="text-[9px] text-muted-foreground/50 ml-0.5">needs prod URL</span>
            )}
          </button>
        </div>
      )}

      {/* ── "What will be replayed" context — shown before run ─────── */}
      {!isActive && !isDone && (
        <div className="flex items-start gap-3 rounded-md bg-muted/40 border border-border/60 px-3 py-2 text-[11px] text-muted-foreground">
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 w-full">
            <span className="text-muted-foreground/50">inputs</span>
            <span>original captured arguments</span>
            <span className="text-muted-foreground/50">env</span>
            <span>recorded env snapshot, decrypted locally</span>
            <span className="text-muted-foreground/50">code</span>
            <span>current version on agent</span>
            {originalDurationMs !== undefined && (
              <>
                <span className="text-muted-foreground/50">original</span>
                <span className="font-mono">{formatDuration(originalDurationMs)}</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Agent URL selector — hidden once running ──────────────────── */}
      {!isActive && !isDone && (
        <div className="space-y-2">
          {/* Production */}
          <div
            role="radio"
            aria-checked={selected === 'production'}
            onClick={(): false | void => productionUrl != null && setSelected('production')}
            onKeyDown={(e): false | void =>
              (e.key === 'Enter' || e.key === ' ') &&
              productionUrl != null &&
              setSelected('production')
            }
            tabIndex={productionUrl != null ? 0 : -1}
            className={cn(
              'rounded-lg border p-3 transition-colors',
              selected === 'production'
                ? 'border-[var(--flux-accent)] bg-[var(--flux-accent)]/5'
                : 'border-border hover:border-muted-foreground/40',
              productionUrl == null ? 'opacity-40 cursor-default' : 'cursor-pointer',
            )}
          >
            <div className="flex items-center gap-2.5">
              <Globe
                className={cn(
                  'h-3.5 w-3.5 shrink-0',
                  selected === 'production' ? 'text-[var(--flux-accent)]' : 'text-muted-foreground',
                )}
              />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium">Production</p>
                {productionUrl != null ? (
                  <p className="font-mono text-[11px] text-muted-foreground truncate">
                    {productionUrl}
                  </p>
                ) : (
                  <p className="text-[11px] text-muted-foreground/50">
                    Not configured —{' '}
                    <Link
                      to={`/projects/${projectId}`}
                      className="underline hover:text-foreground"
                      onClick={(): void => onOpenChange(false)}
                    >
                      set in Project Settings <ExternalLink className="inline h-2.5 w-2.5" />
                    </Link>
                  </p>
                )}
              </div>
              {selected === 'production' && productionUrl != null && (
                <div className="h-4 w-4 rounded-full bg-[var(--flux-accent)] flex items-center justify-center shrink-0">
                  <Check className="h-2.5 w-2.5 text-white" />
                </div>
              )}
            </div>
          </div>

          {/* Local / Dev */}
          <div
            role="radio"
            aria-checked={selected === 'session'}
            onClick={(): false | void => runMode !== 'resume' && setSelected('session')}
            onKeyDown={(e): false | void =>
              (e.key === 'Enter' || e.key === ' ') && runMode !== 'resume' && setSelected('session')
            }
            tabIndex={runMode === 'resume' ? -1 : 0}
            className={cn(
              'rounded-lg border p-3 transition-colors',
              runMode === 'resume' ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer',
              selected === 'session' && runMode !== 'resume'
                ? 'border-[var(--flux-accent)] bg-[var(--flux-accent)]/5'
                : 'border-border hover:border-muted-foreground/40',
            )}
          >
            <div className="flex items-start gap-2.5">
              <Laptop
                className={cn(
                  'h-3.5 w-3.5 mt-0.5 shrink-0',
                  selected === 'session' ? 'text-[var(--flux-accent)]' : 'text-muted-foreground',
                )}
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1.5">
                  <p className="text-xs font-medium leading-none">Local agent</p>
                  <span className="text-[9px] text-muted-foreground/40 font-mono">
                    · session only
                  </span>
                </div>
                {!editingSession && sessionUrl ? (
                  <div className="flex items-center gap-2">
                    <p className="font-mono text-[11px] text-muted-foreground truncate flex-1">
                      {sessionUrl}
                    </p>
                    <button
                      type="button"
                      onClick={(e): void => {
                        e.stopPropagation();
                        setSessionDraft(sessionUrl);
                        setEditingSession(true);
                      }}
                      className="text-[10px] text-muted-foreground/50 hover:text-foreground transition-colors shrink-0"
                    >
                      edit
                    </button>
                    <button
                      type="button"
                      onClick={(e): void => {
                        e.stopPropagation();
                        clearSession();
                      }}
                      className="text-muted-foreground/30 hover:text-foreground transition-colors shrink-0"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <div
                    className="flex items-center gap-2"
                    onClick={(e): void => e.stopPropagation()}
                  >
                    <Input
                      value={sessionDraft}
                      onChange={(e): void => setSessionDraft(e.target.value)}
                      onKeyDown={(e): void => {
                        if (e.key === 'Enter') saveSession();
                        if (e.key === 'Escape') {
                          setEditingSession(false);
                          setSessionDraft(sessionUrl);
                        }
                      }}
                      placeholder="http://localhost:3001/api/flux-agent"
                      className="h-7 text-[11px] font-mono"
                      autoFocus={selected === 'session'}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-[11px] shrink-0"
                      onClick={saveSession}
                      disabled={!sessionDraft.trim()}
                    >
                      Save
                    </Button>
                  </div>
                )}
              </div>
              {selected === 'session' && sessionUrl && !editingSession && (
                <div className="h-4 w-4 rounded-full bg-[var(--flux-accent)] flex items-center justify-center shrink-0">
                  <Check className="h-2.5 w-2.5 text-white" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Active / done URL pill ─────────────────────────────────────── */}
      {(isActive || isDone) && (
        <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
          {selected === 'production' ? (
            <Globe className="h-3 w-3 shrink-0" />
          ) : (
            <Laptop className="h-3 w-3 shrink-0" />
          )}
          <span className="font-mono truncate">{activeUrl}</span>
        </div>
      )}

      {/* ── Execution steps timeline ───────────────────────────────────── */}
      {(isActive || isDone) && (
        <div className="rounded-md border border-border bg-[#0d0d0d] overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border/60">
            <Activity className="h-3 w-3 text-muted-foreground/50" />
            <span className="text-[10px] text-muted-foreground/50 font-mono uppercase tracking-wider">
              execution steps
            </span>
            {replay.status === 'running' && (
              <span className="ml-auto flex items-center gap-1.5 text-[10px] text-muted-foreground/50">
                <Loader2 className="h-2.5 w-2.5 animate-spin" />
                {formatDuration(replay.elapsedMs)}
              </span>
            )}
            {replay.status !== 'running' && steps.length > 0 && (
              <span className="ml-auto text-[10px] text-muted-foreground/30 font-mono">
                {steps.length} events
              </span>
            )}
          </div>
          {/* Steps list */}
          <div className="max-h-56 min-h-[3.5rem] overflow-y-auto py-2">
            {steps.length === 0 ? (
              <p className="px-3 text-[10px] text-muted-foreground/30 font-mono italic py-1">
                {replay.status === 'loading'
                  ? 'starting…'
                  : isDone
                    ? 'no steps recorded'
                    : 'waiting for events…'}
              </p>
            ) : (
              steps.map((step, i): import('react/jsx-runtime').JSX.Element => {
                const isSlow = step.durationMs !== undefined && step.durationMs >= 200;
                const isVerySlow = step.durationMs !== undefined && step.durationMs >= 800;
                const isError = step.phase === 'error';
                return (
                  <div
                    key={`${step.id}-${step.phase}-${i}`}
                    className={cn(
                      'px-3 py-1 transition-colors',
                      isError && 'bg-[var(--flux-error)]/5',
                      isVerySlow && !isError && 'bg-amber-500/5',
                    )}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {/* Capability dot */}
                      <span
                        className="h-[6px] w-[6px] rounded-full shrink-0"
                        style={{
                          backgroundColor: isError
                            ? 'var(--flux-error)'
                            : stepColor(step.capability),
                        }}
                      />
                      {/* Label */}
                      <span
                        className="flex-1 text-[11px] font-mono truncate"
                        style={{
                          color: isError ? 'var(--flux-error)' : stepColor(step.capability),
                        }}
                      >
                        {stepLabel(step.capability, step.action, step.phase)}
                      </span>
                      {/* Phase */}
                      <span
                        className={cn(
                          'text-[9px] font-mono shrink-0 w-16 text-right',
                          isError ? 'text-[var(--flux-error)]/60' : 'text-muted-foreground/35',
                        )}
                      >
                        {step.phase}
                      </span>
                      {/* Duration — amber if slow */}
                      {step.durationMs !== undefined && step.durationMs > 0 && (
                        <span
                          className={cn(
                            'text-[9px] font-mono shrink-0 w-10 text-right',
                            isError
                              ? 'text-[var(--flux-error)]/50'
                              : isVerySlow
                                ? 'text-amber-400'
                                : isSlow
                                  ? 'text-amber-500/60'
                                  : 'text-muted-foreground/25',
                          )}
                        >
                          {formatDuration(step.durationMs)}
                        </span>
                      )}
                    </div>
                    {/* Preview line */}
                    {step.preview != null && (
                      <p
                        className={cn(
                          'ml-[14px] text-[10px] font-mono truncate leading-[1.4]',
                          isError ? 'text-[var(--flux-error)]/60' : 'text-muted-foreground/40',
                        )}
                      >
                        {step.preview}
                      </p>
                    )}
                  </div>
                );
              })
            )}
            <div ref={stepsEndRef} />
          </div>
        </div>
      )}

      {/* ── Result banner ──────────────────────────────────────────────── */}
      {replay.status === 'success' &&
        ((): import('react/jsx-runtime').JSX.Element => {
          const replayMs = replay.durationMs;
          const hasComparison =
            originalDurationMs !== undefined && originalDurationMs > 0 && replayMs > 0;
          const delta = hasComparison ? originalDurationMs - replayMs : 0;
          const pct = hasComparison ? Math.round((delta / originalDurationMs) * 100) : 0;
          const faster = delta > 0;
          return (
            <div className="flex items-start gap-2.5 rounded-lg bg-[var(--flux-success)]/10 border border-[var(--flux-success)]/20 px-3 py-2">
              <Check className="h-3.5 w-3.5 text-[var(--flux-success)] shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-[var(--flux-success)]">Replay complete</p>
                {hasComparison ? (
                  <div className="flex items-baseline gap-3 mt-0.5">
                    <span className="text-[10px] text-[var(--flux-success)]/60">
                      <span className="line-through text-muted-foreground/40">
                        {formatDuration(originalDurationMs)}
                      </span>
                      {' → '}
                      <span className="font-mono font-medium">{formatDuration(replayMs)}</span>
                    </span>
                    <span
                      className={cn(
                        'text-[10px] font-mono font-medium',
                        faster ? 'text-[var(--flux-success)]' : 'text-amber-400',
                      )}
                    >
                      {faster ? `↓ ${pct}%` : `↑ ${Math.abs(pct)}%`}
                    </span>
                  </div>
                ) : (
                  <p className="text-[10px] text-[var(--flux-success)]/70">
                    Finished in {formatDuration(replayMs)}
                  </p>
                )}
              </div>
            </div>
          );
        })()}
      {replay.status === 'error' && (
        <div className="flex items-start gap-2.5 rounded-lg bg-[var(--flux-error)]/10 border border-[var(--flux-error)]/20 px-3 py-2">
          <AlertTriangle className="h-3.5 w-3.5 text-[var(--flux-error)] shrink-0 mt-0.5" />
          <p className="text-xs text-[var(--flux-error)] break-all leading-relaxed">
            {replay.message}
          </p>
        </div>
      )}

      <DialogFooter
        showCloseButton={!embedded && isDone}
        className={embedded ? 'mt-auto' : undefined}
      >
        {isDone ? (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={(): void => setReplay({ status: 'idle' })}
          >
            <RefreshCw className="h-3 w-3" />
            Run Again
          </Button>
        ) : (
          <Button onClick={runReplay} disabled={!canRun} size="sm" className="gap-1.5">
            {isActive ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : runMode === 'resume' ? (
              <FastForward className="h-3.5 w-3.5" />
            ) : (
              <Play className="h-3.5 w-3.5" />
            )}
            {replay.status === 'loading'
              ? 'Starting…'
              : replay.status === 'running'
                ? runMode === 'resume'
                  ? 'Resuming…'
                  : 'Running…'
                : runMode === 'resume'
                  ? 'Resume'
                  : 'Run Replay'}
          </Button>
        )}
      </DialogFooter>
    </>
  );

  if (embedded) {
    return (
      <div
        className={cn(
          'flex h-full min-h-0 w-full max-w-lg flex-col rounded-xl bg-popover p-5 text-sm text-popover-foreground ring-1 ring-foreground/10',
          embeddedClassName,
        )}
      >
        <div className="flex min-h-0 flex-1 flex-col gap-3">{content}</div>
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" showCloseButton>
        {content}
      </DialogContent>
    </Dialog>
  );
}
